import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { FullWidthListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { useModal } from '@r1-frontend/shared/hooks/useModal';

import { POLICY_PAGE_LINK } from '~/src/constants';
import {
    BonusesPayment,
    PaymentFieldWrapper,
    PaymentSumInput,
    TwoFieldWrapper,
} from '~/src/features/payments-abn/components/PaymentForms';
import { BonusesPayButton } from '~/src/features/payments-abn/components/PaymentForms/components/BonusesPayment/BonusesPayButton';
import { useSendBonusesPayment } from '~/src/features/payments-abn/components/PaymentForms/components/BonusesPayment/useSendBonusesPayment';
import { PhoneInput } from '~/src/features/payments-abn/components/PaymentForms/components/PhoneInput';
import { TState } from '~/src/store';

import { SberPayButton } from './SberPayButton';
import { SberPayInstruction } from './SberPayInstruction';
import { SberPayErrorPopup, SberPaySuccessPopup } from './SberPayResultPopup';
import { useSberPayMethod } from './useSberPayMethod';
import { useSberPayTab } from './useSberPayTab';

/**
 * Добавляет метод оплаты с помощью СберПэй
 */
export const SberPay = (): JSX.Element | null => {
    const {
        isAuth,
        showSberPay,
        phone,
        bonusesValue,
        paymentSum,
    } = useSelector((state: TState) => ({
        isAuth: state.auth.isAuth,
        paymentSum: state.payment.paymentData.paymentSum,
        showSberPay: state.payment.method?.methodType === 'sberpay',
        phone: state.payment.paymentData.phone,
        bonusesValue: state.payment.paymentData.bonusesValue,
    }), shallowEqual);

    const { isOpen: isOpenSuccess, openModal: openSuccessModal, closeModal: closeSuccessModal } = useModal();
    const { isOpen: isOpenError, openModal: openErrorModal, closeModal: closeErrorModal } = useModal();

    const { isOnlyBonusesPay, sendBonusesPayment } = useSendBonusesPayment();

    useSberPayTab();

    const { isValid, sendSberPay } = useSberPayMethod();

    const canPayWithBonuses = useMemo(() => {
        const maxBonusPayment = paymentSum / 0.2;
        return !!paymentSum && bonusesValue && bonusesValue <= maxBonusPayment;
    }, [bonusesValue, paymentSum]);

    const onSendSberPay = async() => {
        closeSuccessModal();
        closeErrorModal();

        sendBonusesPayment();

        const isSuccess = await sendSberPay();

        if (isSuccess) {
            openSuccessModal();
        } else {
            openErrorModal();
        }
    };

    if (!showSberPay) {
        return null;
    }

    return (
        <>
        <FullWidthListContainer $direction='column' $size={24}>
            <H3 $withIndent={false}>Детали оплаты</H3>

            <PaymentFieldWrapper>
                <TwoFieldWrapper>
                    <PaymentSumInput/>
                    <PhoneInput/>
                </TwoFieldWrapper>

                <BonusesPayment/>
            </PaymentFieldWrapper>
            {!isOnlyBonusesPay &&
                <SberPayInstruction/>
            }

            <FullWidthListContainer $direction='column' $size={8}>
                {!isAuth &&
                    <PolicyNote
                        data-test='pay-agreement'
                        buttonText='Оплатить'
                        agreeUrl={POLICY_PAGE_LINK}
                    />
                }
                <ButtonGroup>
                    {isOnlyBonusesPay
                        ? <BonusesPayButton
                            btnText={'Оплатить бонусами'}
                            isDisable={!canPayWithBonuses}
                            onClick={sendBonusesPayment}
                        />
                        : <SberPayButton
                            isDisable={!isValid}
                            onClick={onSendSberPay}
                        />
                    }
                </ButtonGroup>
            </FullWidthListContainer>
        </FullWidthListContainer>

        {isOpenSuccess && !isOpenError &&
            <SberPaySuccessPopup
                phone={phone}
                onClick={onSendSberPay}
                onClose={closeSuccessModal}
            />
        }
        {isOpenError && !isOpenSuccess &&
            <SberPayErrorPopup
                phone={phone}
                onClick={onSendSberPay}
                onClose={closeErrorModal}
            />
        }
        </>
    );
};
